import { useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import Chip from '../../../components/Chip.jsx';
import DataTable from '../../../components/DataTable.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ErrorNote from '../ErrorNote.jsx';
import { engineConfig } from '../../../utils/engineConfig.js';
import useRunResult from '../../../utils/useRunResult.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

/**
 * Search mode: random search with early stopping over a param space, sampling
 * the same grid space the Optimize mode exhausts — but evaluating a random
 * subset and stopping when improvement stalls (server-side, metered per
 * sample). Self-owned alternative to a paid third-party optimizer.
 */
export default function SearchMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun, rulesStrategy }) {
  const [paramName, setParamName] = useState('config.default_max_hold_bars');
  const [gridMin, setGridMin] = useState(10);
  const [gridMax, setGridMax] = useState(50);
  const [gridStep, setGridStep] = useState(10);
  const [iterations, setIterations] = useState(30);
  const [stopPatience, setStopPatience] = useState(6);
  const [seed, setSeed] = useState(1);
  const [strategy, setStrategy] = useState('random');
  const [objective, setObjective] = useState('sharpe');
  const [maximize, setMaximize] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result);
  const [error, setError] = useState(null);

  const request = {
    base: {
      mode: 'rules',
      strategy: rulesStrategy,
      symbols: config.symbols,
      period: config.period,
      config: engineConfig(config),
    },
    paramRanges: { [paramName]: { min: gridMin, max: gridMax, step: gridStep || 1 } },
    strategy,
    objective,
    maximize,
    iterations,
    stopPatience,
    seed,
  };

  async function run() {
    setError(null);
    setRunning(true);
    try {
      const data = await backtestApi.search(request);
      setResult(data);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }

  const res = result?.result || null;
  const columns = [
    { key: 'params', header: 'Params', render: (r) => JSON.stringify(r.params) },
    { key: 'totalReturn', header: 'Return', numeric: true, render: (r) => `${(r.totalReturn ?? 0).toFixed(2)}%` },
    { key: 'sharpe', header: 'Sharpe', numeric: true, render: (r) => (r.sharpe != null ? r.sharpe.toFixed(2) : '—') },
    { key: 'maxDrawdown', header: 'Max DD', numeric: true, render: (r) => `${(r.maxDrawdown ?? 0).toFixed(2)}%` },
    { key: 'objective', header: `Objective (${res?.objective || objective})`, numeric: true, render: (r) => (r.objective != null ? r.objective.toFixed(2) : '—') },
  ];

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Search · early stopping
          </h3>
          <p className="aq-module-desc" style={{ marginBottom: 10 }}>
            Samples the same space the Optimize grid exhausts and stops when improvement stalls —
            cheaper and scales to many params. <strong>Random</strong> explores uniformly;
            <strong> Bayesian</strong> (TPE-lite) learns from early samples and proposes values near the
            best region, converging faster on many params. Each sample is metered like a normal backtest;
            early stop = fewer samples = less credit.
          </p>
          <div className="aq-bt-params">
            <label className="aq-field">
              <span>Parameter</span>
              <select value={paramName} onChange={(e) => setParamName(e.target.value)}>
                <option value="config.default_max_hold_bars">Maximum holding bars</option>
                <option value="config.default_sl_pct">Stop-loss fraction</option>
                <option value="config.default_pt_pct">Profit-target fraction</option>
                <option value="config.slippage_mult">Slippage multiplier</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Min</span>
              <input type="number" value={gridMin} onChange={(e) => setGridMin(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Max</span>
              <input type="number" value={gridMax} onChange={(e) => setGridMax(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Step</span>
              <input type="number" value={gridStep} onChange={(e) => setGridStep(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Sample budget</span>
              <input type="number" min="1" value={iterations} onChange={(e) => setIterations(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Stop patience</span>
              <input type="number" min="1" value={stopPatience} onChange={(e) => setStopPatience(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Seed (reproducible)</span>
              <input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Search strategy</span>
              <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
                <option value="random">Random</option>
                <option value="bayesian">Bayesian (TPE-lite)</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Objective</span>
              <select value={objective} onChange={(e) => setObjective(e.target.value)}>
                <option value="sharpe">Sharpe</option>
                <option value="totalReturn">Total return</option>
                <option value="cagr">CAGR</option>
                <option value="profitFactor">Profit factor</option>
                <option value="calmar">Calmar</option>
                <option value="trades">Trades</option>
                <option value="maxDrawdown">Max drawdown</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Direction</span>
              <select value={maximize ? 'max' : 'min'} onChange={(e) => setMaximize(e.target.value === 'max')}>
                <option value="max">Maximize</option>
                <option value="min">Minimize</option>
              </select>
            </label>
          </div>

          <StrategyPreview title="search request preview" value={request} />

          <div style={{ marginTop: 14 }}>
            <Button disabled={running || !rulesStrategy || !isBacktestPeriodValid(config.period)} onClick={run}>
              {running ? 'Searching…' : `Run search (≤ ${Math.max(1, iterations)} samples)`}
            </Button>
            {!rulesStrategy && <p className="aq-note">Build a strategy in Rules Builder before searching its parameters.</p>}
          </div>
        </Card>
      </div>

      <Card>
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
          Leaderboard{res?.strategy ? ` · ${res.strategy === 'bayesian' ? 'Bayesian (TPE-lite)' : 'Random'}` : ''}
        </h3>
        <ErrorNote error={error} />
        {!res && !error && <p className="aq-module-desc">Run a search to see the sampled leaderboard.</p>}
        {res && (
          <>
            {(res.earlyStopped || res.stoppedReason) && (
              <p className="aq-news-meta" style={{ marginBottom: 8 }}>
                {res.stoppedReason === 'insufficient_credit'
                  ? 'Stopped: compute credit ran out mid-search.'
                  : `Stopped early after ${res.plan.sampled} samples (no improvement for ${res.plan.stopPatience} in a row).`}{' '}
                Evaluated {res.plan.sampled} of {res.plan.totalCombos} possible combos.
              </p>
            )}
            <DataTable
              columns={columns}
              rows={res.leaderboard.map((r, i) => ({ ...r, id: i }))}
              emptyLabel="No results."
            />
            {res.best && (
              <div className="aq-metric-row" style={{ marginTop: 10 }}>
                <span>
                  Best: <strong>{JSON.stringify(res.best.params)}</strong>
                </span>
                <Chip tone="gain">Objective {(res.best.objective ?? 0).toFixed(2)}</Chip>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
